import React, { useEffect, useState } from "react";
import api, { formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Download, FileArrowDown, DatabaseIcon, PlayCircle, Clock, Warning,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function humanSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function Backup() {
  const { user: authUser } = useAuth();
  const isSuperAdmin = authUser?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [wiping, setWiping] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/backup/list");
      setItems(data);
    } catch {
      /* backup list yoksa sessizce geç */
    }
  };

  useEffect(() => { load(); }, []);

  const wipeTransactional = async () => {
    // 3 aşamalı onay — kazayı imkansız kıl
    if (!window.confirm(
      `⚠️ TÜM CIRO VERİLERİ SIFIRLANACAK\n\n` +
      `Silinecek:\n` +
      `  • Oturumlar, tahsilatlar, abonelik ödemeleri\n` +
      `  • Banka aktarımları, giderler, manuel gelirler\n` +
      `  • Kamera okumaları, eşleşmeyen çıkışlar\n` +
      `  • Denetim (audit) kayıtları\n` +
      `  • Plaka abonelikleri ve doğrulama istekleri\n\n` +
      `Korunacak:\n` +
      `  • Kullanıcılar, tarifeler, planlar, kurumsal hesaplar\n` +
      `  • Katlar/otoparklar, kameralar, kupon kodları, ayarlar\n\n` +
      `İşlemden önce OTOMATİK YEDEK alınır.\n\n` +
      `Devam etmek için TAMAM'a basın.`
    )) return;
    const step2 = window.prompt(
      `Son onay — bu işlem GERİ ALINAMAZ.\n\n` +
      `Onay için tam olarak şunu yazın: SIFIRLA-EVET`, ""
    );
    if (step2 !== "SIFIRLA-EVET") { toast.info("İşlem iptal edildi"); return; }
    setWiping(true);
    try {
      const { data } = await api.post(
        "/backup/wipe-transactional?confirm=WIPE-EVET-BILIYORUM&take_backup=true"
      );
      toast.success(
        `${data.total_deleted} kayıt silindi. ` +
        `Yedek: ${data.backup?.json_file || "N/A"}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıfırlama başarısız");
    } finally { setWiping(false); }
  };

  const downloadFresh = async (fmt) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("otopark_token");
      const res = await fetch(`${BACKEND_URL}/api/backup/${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "_");
      a.href = url;
      a.download = `otopark_backup_${ts}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} yedeği indirildi`);
    } catch (e) {
      toast.error("İndirme başarısız");
    } finally {
      setBusy(false);
    }
  };

  const downloadSaved = async (filename) => {
    try {
      const token = localStorage.getItem("otopark_token");
      const res = await fetch(`${BACKEND_URL}/api/backup/download/${filename}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("İndirilemedi"); }
  };

  const runNow = async () => {
    setBusy(true);
    try {
      await api.post("/backup/run-now");
      toast.success("Yedek oluşturuldu");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Yedek</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Verileri Yedekle / Dışa Aktar</h1>
        <div className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Tüm oturumlar, aboneler, işlemler, tarifeler, kullanıcılar, denetim
          kayıtları — hepsi bir Excel veya JSON dosyasında. Otomatik günlük
          yedek her gece 23:30&apos;da alınır (son 30 yedek saklanır).
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-sm border-border">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <FileArrowDown size={24} weight="fill" className="text-primary" />
              <h3 className="font-semibold uppercase tracking-widest text-sm">Excel İndir</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Her koleksiyon ayrı sayfada. Elle inceleme ve muhasebe için.
            </p>
            <Button
              data-testid="backup-download-xlsx"
              onClick={() => downloadFresh("xlsx")}
              disabled={busy}
              className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-semibold"
            >
              <Download size={16} /> Excel İndir
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-border">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <DatabaseIcon size={24} weight="fill" className="text-primary" />
              <h3 className="font-semibold uppercase tracking-widest text-sm">JSON İndir</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Yeniden içe aktarma / arşiv / başka sisteme taşıma için tam veri.
            </p>
            <Button
              data-testid="backup-download-json"
              onClick={() => downloadFresh("json")}
              disabled={busy}
              variant="outline"
              className="w-full rounded-sm uppercase tracking-widest font-semibold"
            >
              <Download size={16} /> JSON İndir
            </Button>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-border">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <PlayCircle size={24} weight="fill" className="text-primary" />
              <h3 className="font-semibold uppercase tracking-widest text-sm">Şimdi Yedekle</h3>
            </div>
            <p className="text-xs text-muted-foreground">
              Sunucuda anında bir yedek dosyası oluşturur. Aşağıdaki listede görünür.
            </p>
            <Button
              data-testid="backup-run-now"
              onClick={runNow}
              disabled={busy}
              variant="outline"
              className="w-full rounded-sm uppercase tracking-widest font-semibold"
            >
              <PlayCircle size={16} /> Yedeği Oluştur
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-muted-foreground" />
              <h3 className="font-semibold uppercase tracking-widest text-sm">
                Otomatik Yedekler ({items.length})
              </h3>
            </div>
            <div className="text-xs text-muted-foreground">Her gün 23:30 — son 30 saklanır</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dosya</TableHead>
                <TableHead>Tip</TableHead>
                <TableHead>Boyut</TableHead>
                <TableHead>Oluşturuldu</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Henüz otomatik yedek yok. &quot;Yedeği Oluştur&quot; ile ilk yedeği alabilirsiniz.
                  </TableCell>
                </TableRow>
              )}
              {items.map((it) => (
                <TableRow key={it.filename}>
                  <TableCell className="font-mono text-xs">{it.filename}</TableCell>
                  <TableCell>
                    <Badge
                      variant={it.kind === "xlsx" ? "default" : "outline"}
                      className="rounded-sm uppercase text-[10px] tracking-widest"
                    >
                      {it.kind}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{humanSize(it.size)}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(it.created_at)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => downloadSaved(it.filename)}
                      data-testid={`backup-dl-${it.filename}`}
                    >
                      <Download size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isSuperAdmin && (
        <Card className="rounded-sm border-destructive/50 bg-destructive/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Warning size={22} weight="fill" className="text-destructive" />
              <h3 className="font-semibold uppercase tracking-widest text-sm text-destructive">Tehlikeli Bölge — Süperadmin</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Tüm ciro (transactional) verilerini toplu sıfırlar: oturumlar, tahsilatlar,
              abonelik ödemeleri, banka aktarımları, giderler, manuel gelirler,
              kamera okumaları, eşleşmeyen çıkışlar, denetim kayıtları ve plaka
              abonelikleri. <strong>Yapılandırma (kullanıcılar, tarifeler, planlar,
              kurumsallar, katlar, kameralar, kuponlar, ayarlar) korunur.</strong>
              İşlem öncesi otomatik yedek alınır. Geri alınamaz.
            </p>
            <Button
              variant="destructive"
              onClick={wipeTransactional}
              disabled={wiping}
              data-testid="backup-wipe-transactional"
              className="rounded-sm uppercase tracking-widest text-xs"
            >
              <Warning size={14} weight="bold" />
              {wiping ? "Sıfırlanıyor..." : "Tüm Ciro Verilerini Sıfırla"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
